// import YAxis from "./YAxis";
// import Grid from "./Grid";
// import Circle from "./Circle";
// import XAxis from "./XAxis";
// import Tooltip from "./Tooltip";
// import ValueLabel from "./ValueLabel";
import React, { useRef, useState, useLayoutEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

//---------------- Y Axis (like BarChart) ----------------
const YAxis = ({ ticks, onWidthChange, formatTick }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(() => {
      if (ref.current) {
        onWidthChange(ref.current.offsetWidth);
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [onWidthChange]);

  return (
    <div
      ref={ref}
      className="flex flex-col-reverse justify-between items-end h-full pr-2"
    >
      {ticks.map((t, i) => (
        <span key={i} className="text-xs text-gray-600 whitespace-nowrap">
          {formatTick ? formatTick(t) : t}
        </span>
      ))}
    </div>
  );
};

// ---------------- X Axis ----------------
const XAxis = ({ points, marginLeft, formatTick }) => (
  <div className="relative min-h-5 mt-3" style={{ marginLeft }}>
    {points.map((p, i) => (
      <span
        key={i}
        className="absolute text-xs text-gray-600 font-medium whitespace-nowrap text-center"
        style={{
          left: p.x,
          transform: "translateX(-50%)",
        }}
      >
        {formatTick ? formatTick(p.label) : p.label}
      </span>
    ))}
  </div>
);

// ---------------- Grid ----------------
const Grid = ({
  ticks,
  points,
  width,
  height,
  padding,
  showHorizontal,
  showVertical,
}) => (
  <g>
    {showHorizontal &&
      ticks.map((_, i) => {
        const y =
          padding.top +
          ((height - padding.top - padding.bottom) / (ticks.length - 1)) * i;
        return (
          <line
            key={`h-${i}`}
            x1={padding.left}
            x2={width - padding.right}
            y1={y}
            y2={y}
            stroke="#DBDBDB"
            strokeDasharray="4 4"
          />
        );
      })}

    {showVertical &&
      points.map((p, i) => (
        <line
          key={`v-${i}`}
          x1={p.x}
          x2={p.x}
          y1={padding.top}
          y2={height - padding.bottom}
          stroke="#DBDBDB"
          strokeDasharray="4 4"
        />
      ))}
  </g>
);

// ---------------- Circles ----------------
const Circle = ({ points, strokeColor, onHover, isHover }) => (
  <g>
    {points.map((p, i) => (
      <motion.circle
        key={i}
        cx={p.x}
        cy={p.y}
        r={6}
        fill="white"
        stroke={strokeColor}
        strokeWidth={2}
        onMouseEnter={(e) => onHover(p)}
        onMouseMove={(e) => onHover(p)}
        onMouseLeave={() => onHover(null)}
        style={{ cursor: isHover ? "pointer" : "" }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3, delay: i * 0.05 }} // ✅ staggered pop-in
      />
    ))}
  </g>
);

// ---------------- Tooltip ----------------
const Tooltip = ({ point, containerRef, renderToolTip }) => {
  const tooltipRef = useRef(null);
  if (!point || !containerRef.current) return null;

  const containerRect = containerRef.current.getBoundingClientRect();
  const tooltipEl = tooltipRef.current;
  const tooltipW = tooltipEl?.offsetWidth || 120;
  const tooltipH = tooltipEl?.offsetHeight || 120;

  let top = point.y;
  let left = point.x;

  // Check if enough space above
  if (point.y - tooltipH - 8 > 0) {
    top = point.y - tooltipH - 8; // place above
    left = point.x - tooltipW / 2; // center align
  }
  // Else try right side
  else if (point.x + tooltipW + 8 < containerRect.width) {
    top = point.y - tooltipH / 2; // vertically center
    left = point.x + 8; // right side
  }
  // Else try left side
  else if (point.x - tooltipW - 8 > 0) {
    top = point.y - tooltipH / 2;
    left = point.x - tooltipW - 8; // left side
  }
  // Else fallback: place below
  else {
    top = point.y + 8;
    left = point.x - tooltipW / 2;
  }

  //Clamp inside container
  top = Math.max(0, Math.min(top, containerRect.height - tooltipH));
  left = Math.max(0, Math.min(left, containerRect.width - tooltipW));

  return (
    <AnimatePresence>
      {point && (
        <motion.div
          ref={tooltipRef}
          key={point.label}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.2 }}
          className="absolute z-10 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-400"
          style={{
            left,
            top,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {renderToolTip(point)}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ---------------- Value Label ----------------
const ValueLabel = ({ point, containerRef, color }) => {
  const labelRef = useRef(null);
  if (!point || !containerRef.current) return null;

  const containerRect = containerRef.current.getBoundingClientRect();
  const labelEl = labelRef.current;
  const labelW = labelEl?.offsetWidth || 40;
  const labelH = labelEl?.offsetHeight || 20;

  let top = point.y - labelH - 6; // default above circle
  let left = point.x - labelW / 2;

  // If no space above, try right
  if (top < 0) {
    top = point.y - labelH / 2;
    left = point.x + 8;
  }

  // If no space right, try left
  if (left + labelW > containerRect.width) {
    left = point.x - labelW - 8;
    top = point.y - labelH / 2;
  }

  // Clamp inside chart
  top = Math.max(0, Math.min(top, containerRect.height - labelH));
  left = Math.max(0, Math.min(left, containerRect.width - labelW));

  return (
    <motion.div
      ref={labelRef}
      key={point.label}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
      className={`absolute border border-[${color}] text-[${color}] px-2 py-0.5 rounded text-xs font-medium -mt-2`}
      style={{
        top,
        left,
        whiteSpace: "nowrap",
        pointerEvents: "none",
      }}
    >
      {point.value}
    </motion.div>
  );
};

const buildPath = (points, height, padding) => {
  if (points.length === 0) return "";

  const baseline = height - padding.bottom;
  const allPoints = [{ x: 0, y: baseline }, ...points];

  let d = `M ${allPoints[0].x},${allPoints[0].y}`;

  for (let i = 0; i < allPoints.length - 1; i++) {
    const p0 = allPoints[i - 1] || allPoints[i];
    const p1 = allPoints[i];
    const p2 = allPoints[i + 1];
    const p3 = allPoints[i + 2] || p2;

    // const cp1x = p1.x + (p2.x - p0.x) / 6;
    // const cp1y = p1.y + (p2.y - p0.y) / 6;

    // const cp2x = p2.x - (p3.x - p1.x) / 6;
    // const cp2y = p2.y - (p3.y - p1.y) / 6;

    let cp1x = p1.x + (p2.x - p0.x) / 6;
    let cp1y = p1.y + (p2.y - p0.y) / 6;

    let cp2x = p2.x - (p3.x - p1.x) / 6;
    let cp2y = p2.y - (p3.y - p1.y) / 6;

    // ✅ Fix overshoot when y is flat (p1.y === p2.y)
    if (p1.y === p2.y) {
      cp1y = p1.y;
      cp2y = p2.y;
    }

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  const last = points[points.length - 1];
  d += ` L ${last.x},${baseline} L 0,${baseline} Z`;

  return d;
};

// ---------------- Main Chart ----------------
const AreaChart = ({
  data,
  steps = 5,
  showHorizontalGrid = true,
  showVerticalGrid = true,
  showValueLabel,
  formatYAxisTick,
  formatXAxisTick,
  gradientFrom = "#E75D01", //Hexcode, RGB
  gradientTo = "#FF781F", //Hexcode, RGB
  strokeColor = "#f97316", //Hexcode, RGB
  isHover = true,
  backgroundColor = "bg-white",
  renderToolTip = () => <div>ToolTip</div>,
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [yAxisWidth, setYAxisWidth] = useState(0);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const padding = { top: 0, right: 0, bottom: 0, left: 0 };

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;

      setContainerSize((prev) => {
        if (prev.width !== width || prev.height !== height) {
          return { width, height };
        }
        return prev;
      });
    });

    observer.observe(containerRef.current);
    const rect = containerRef.current.getBoundingClientRect();
    setContainerSize({ width: rect.width, height: rect.height });

    return () => observer.disconnect();
  }, []);

  const svgWidth = containerSize.width;
  const svgHeight = containerSize.height;

  // const maxValue = Math.max(...data.map((d) => d.value));
  // const step = Math.ceil(maxValue / steps + 1);
  // const maxTick = step * steps;
  // const ticks = Array.from({ length: steps + 1 }, (_, i) => step * i);

  // const maxValue = Math.max(...data.map((d) => d.value));
  // const step = maxValue / (steps - 1);
  // const ticks = Array.from(
  //   { length: steps + 1 },
  //   (_, i) => +(step * i).toFixed(2)
  // );
  // const maxTick = step * steps;

  const maxValue = Math.max(...data.map((d) => d.value));
  const safeMax = maxValue === 0 ? 1 : maxValue; // Prevent divide-by-zero
  const step = safeMax / (steps - 1);
  const ticks = Array.from(
    { length: steps + 1 },
    (_, i) => +(step * i).toFixed(2)
  );
  const maxTick = step * steps;

  const computedPoints = useMemo(() => {
    if (data.length === 0 || svgWidth === 0 || svgHeight === 0) return [];

    const innerW = svgWidth - padding.left - padding.right;
    const innerH = svgHeight - padding.top - padding.bottom;
    const n = data.length;

    const xPadding = innerW * 0.05;
    const usableW = innerW - 2 * xPadding;

    const xs =
      n === 1
        ? [padding.left + innerW / 2]
        : Array.from(
            { length: n },
            (_, i) => padding.left + xPadding + (i / (n - 1)) * usableW
          );

    const ys = data.map((d) => {
      const ratio = Math.max(0, Math.min(1, d.value / maxTick));
      return padding.top + (1 - ratio) * innerH;
    });

    return xs.map((x, i) => ({
      x,
      y: ys[i],
      value: data[i].value,
      label: data[i].label,
      ...data[i], // Modified to send extra data
    }));
  }, [data, svgWidth, svgHeight, padding, maxTick]);

  const path = buildPath(computedPoints, svgHeight, padding);

  return (
    <div className={`flex-1 flex flex-col`}>
      <div className="flex flex-1">
        <YAxis
          ticks={ticks}
          onWidthChange={setYAxisWidth}
          formatTick={formatYAxisTick}
        />

        <div
          ref={containerRef}
          className={`relative flex-1 ${backgroundColor} border border-gray-300 rounded-lg overflow-hidden`}
        >
          <div className="absolute inset-0">
            <svg width={svgWidth} height={svgHeight}>
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={gradientFrom}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="100%"
                    stopColor={gradientTo}
                    stopOpacity={0.3}
                  />
                </linearGradient>
              </defs>

              <Grid
                ticks={ticks}
                points={computedPoints}
                width={svgWidth}
                height={svgHeight}
                padding={padding}
                showHorizontal={showHorizontalGrid}
                showVertical={showVerticalGrid}
              />

              <motion.path
                d={path}
                fill="url(#areaGradient)"
                stroke={strokeColor}
                strokeWidth={2}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              />

              <Circle
                points={computedPoints}
                strokeColor={strokeColor}
                isHover={isHover}
                onHover={setHoveredPoint}
              />
            </svg>
          </div>

          {showValueLabel &&
            computedPoints.map((p, i) => (
              <ValueLabel
                key={`label-${i}`}
                point={p}
                containerRef={containerRef}
                color={strokeColor}
              />
            ))}
          {isHover && (
            <Tooltip
              point={hoveredPoint}
              containerRef={containerRef}
              renderToolTip={renderToolTip}
            />
          )}
        </div>
      </div>

      <XAxis
        points={computedPoints}
        marginLeft={yAxisWidth}
        formatTick={formatXAxisTick}
      />
    </div>
  );
};

export default AreaChart;
